import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Skeleton, notification, Button } from 'src/common';
import { Paths } from 'src/pages/paths';
import styles from '../index.module.scss';
import AuthBack from '../AuthBack';
import { Auth } from 'aws-amplify';
import { tempCognitoUserVar, useApp } from 'src/models/auth';
import { useNavigate } from 'react-router-dom';
import { StorageKey } from 'src/types/enums';

interface FormState {
  code: string;
}

export const Confirm2FAPage: FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { loading: isLoading, handlePaidUser } = useApp();
  const [isDisabled, setIsDisabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const from = sessionStorage.getItem(StorageKey.FromLogin);
  const redirectTo = useMemo(() => from ?? Paths.HOME_PATH, [from]);
  const initialValues = useMemo((): FormState => {
    return {
      code: ''
    };
  }, []);

  const onFinish = useCallback(
    (values: FormState) => {
      const { code } = values;
      if (submitting) {
        return; // do not sent form again while previous request is active
      }
      const authUser = tempCognitoUserVar();
      setSubmitting(true);
      Auth.confirmSignIn(authUser, code, 'SOFTWARE_TOKEN_MFA')
        .then(data => {
          setSubmitting(false);
          handlePaidUser(data).then(() => {
            sessionStorage.removeItem(StorageKey.FromLogin);
            navigate(redirectTo, { replace: true });
          });
        })
        .catch(err => {
          const { code } = err;
          if (code) {
            if (code === 'InvalidParameterException') {
              form.setFields([
                {
                  name: 'code',
                  errors: [t('Auth.codeLengthError')]
                }
              ]);
            } else if (code === 'NotAuthorizedException') {
              form.setFields([
                {
                  name: 'code',
                  errors: [t('Auth.invalidTokenError')]
                }
              ]);
            } else {
              form.setFields([
                {
                  name: 'code',
                  errors: [t('Common.unknownErrorDesc')]
                }
              ]);
            }
          } else {
            notification.error({
              description: t('Common.unknownErrorDesc'),
              message: t('Common.unknownError')
            });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [form, handlePaidUser, navigate, redirectTo, submitting, t]
  );

  const onValuesChange = useCallback(
    (changedValues: Partial<FormState>) => {
      setIsDisabled(!changedValues.code);
    },
    [setIsDisabled]
  );

  return isLoading ? (
    <Skeleton active />
  ) : (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={onFinish}
      onValuesChange={onValuesChange}
      layout={'vertical'}
      className={styles.form}
    >
      <div className={styles.heading}>
        <h3 className={styles.title}>
          <span>{t('Auth.confirmSignIn.title')}</span>
        </h3>
        <span className={styles.desc}>{t('Auth.confirmSignIn.desc')}</span>
      </div>
      <Form.Item
        name="code"
        label={t('Auth.common.labels.code')}
        rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Auth.code') }) }]}
      >
        <Input
          name="code"
          placeholder={t('Auth.common.placeholders.code')}
          autoComplete={'one-time-code'}
          type={'number'}
        />
      </Form.Item>
      <div className={styles.actions}>
        <Button htmlType="submit" type={'primary'} loading={isLoading} disabled={isDisabled} className={styles.button}>
          {t('Auth.common.buttons.login')}
        </Button>
        <AuthBack />
      </div>
    </Form>
  );
};

export default Confirm2FAPage;
